import {
  ActionService,
  ApplicationExchangeRateResponse,
  DefinitionActionKindEnum,
  DefinitionActionSubKindEnum,
} from '@acme/api';
import { Image } from '@acme/assets';
import {
  ApplicationActions,
  UploadDocumentModal,
  findAction,
} from '@acme/features';
import { CopyableText, formatNumber, useModal } from '@acme/foundation';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from 'flowbite-react';
import { twMerge } from 'tailwind-merge';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface ProvidersTableProps {
  providers: ApplicationExchangeRateResponse[];
  className: string;
  applicationId?: string;
  onUploadSuccess?: () => void;
}

export const ApplicationProvidersTable = ({
  providers,
  className,
  applicationId,
  onUploadSuccess,
}: ProvidersTableProps) => {
  const navigate = useNavigate();
  const { openModal, openConfirmModal, closeModal } = useModal();
  const hasAnyActions = providers?.some((p) => (p.actions?.length ?? 0) > 0);
  const hasAnyWalletAddress = providers?.some(
    (p) => p.walletAddress != null && p.walletAddress.trim() !== ''
  );

  const signHandlers = applicationId
    ? {
        signExchangeContract: {
          handler: (actionId?: string) => {
            if (actionId) {
              navigate(`/application/${applicationId}/sign/${actionId}`);
            }
          },
        },
      }
    : undefined;

  const openUploadAdminToProviderModal = useCallback(
    (actionId: string) => {
      if (!applicationId) return;
      openModal({
        title: 'Загрузка файла',
        size: 'auto',
        content: (
          <UploadDocumentModal
            title="Загрузить документ"
            onConfirm={async (file) => {
              const res = await ActionService.postApiV1ActionUpload({
                id: actionId,
                formData: { files: [file] },
              });
              if (res?.isSuccess) {
                closeModal();
                onUploadSuccess?.();
              } else {
                throw new Error(
                  res?.errorText ?? 'Не удалось отправить документ'
                );
              }
            }}
            onCancel={closeModal}
          />
        ),
        onClose: closeModal,
      });
    },
    [applicationId, openModal, closeModal, onUploadSuccess]
  );

  const getUploadHandlers = useCallback(
    (actions: ApplicationExchangeRateResponse['actions']) => {
      const action = findAction(
        actions ?? [],
        DefinitionActionKindEnum.Upload,
        DefinitionActionSubKindEnum.UploadAdminToProviderDoc
      );
      if (!action?.id) return undefined;
      return {
        uploadAdminToProviderDoc: {
          handler: () => openUploadAdminToProviderModal(action.id!),
        },
      };
    },
    [openUploadAdminToProviderModal]
  );

  const openConfirmModalHandler = useCallback(async (actionId?: string) => {
    if (!actionId) return;
    const confirmed = await openConfirmModal({
      title: 'Подтвердить получение д/с',
      content: 'Денежные средства получены в полном объеме?',
      okBtn: 'Подтвердить',
      cancelBtn: 'Отмена',
    });

    if (!confirmed) return;

    return ActionService.postApiV1ActionConfirm({
      id: actionId,
      requestBody: { comment: null },
    });
  }, []);

  const openConfirmDealUploadModal = useCallback(
    (actionId: string) => {
      openModal({
        title: 'Подтверждение проведения сделки',
        content: (
          <UploadDocumentModal
            title="Загрузить документ"
            onConfirm={async (file) => {
              const res = await ActionService.postApiV1ActionUpload({
                id: actionId,
                formData: { files: [file] },
              });
              if (res?.isSuccess) {
                closeModal();
                onUploadSuccess?.();
              } else {
                throw new Error(
                  res?.errorText ?? 'Не удалось отправить документ'
                );
              }
            }}
            onCancel={closeModal}
          />
        ),
        onClose: closeModal,
      });
    },
    [openModal, closeModal, onUploadSuccess]
  );

  const getConfirmDealUpload = useCallback(
    (actions: ApplicationExchangeRateResponse['actions']) => {
      const action = actions?.find(
        (a) =>
          a.kind === DefinitionActionKindEnum.Upload &&
          (a.subKind === null || a.subKind === undefined)
      );
      if (!action?.id) return undefined;
      return {
        handler: (actionId?: string) => {
          if (actionId) openConfirmDealUploadModal(actionId);
        },
      };
    },
    [openConfirmDealUploadModal]
  );

  const getFormHandlers = useCallback(
    (actions: ApplicationExchangeRateResponse['actions']) => {
      const action = findAction(
        actions ?? [],
        DefinitionActionKindEnum.Form,
        DefinitionActionSubKindEnum.FormTransactionId
      );
      if (!action?.id || !applicationId) return undefined;
      return {
        formTransactionId: {
          handler: () =>
            navigate(
              `/application/${applicationId}/transaction-id/${action.id}`
            ),
        },
      };
    },
    [applicationId, navigate]
  );

  if (!providers?.length) {
    return (
      <div
        className={`${className} flex flex-col items-center justify-center py-12`}
      >
        <div className="flex flex-col gap-4 items-center max-w-[448px]">
          <div className="w-40 overflow-hidden shrink-0">
            <Image
              name="select-provider"
              alt="Выберите поставщика"
              className="w-full h-full object-contain"
              width="100%"
              height="100%"
            />
          </div>
          <p className="text-center text-base text-gray-900">
            Наберите нужную вам сумму из средств
            <br />
            доступных поставщиков
          </p>
        </div>
      </div>
    );
  }

  return (
    <Table className={twMerge('w-full', className)}>
      <TableHead>
        <TableRow>
          <TableHeadCell>Заказ</TableHeadCell>
          <TableHeadCell>Название ПЛ</TableHeadCell>
          <TableHeadCell>Курс обмена</TableHeadCell>
          <TableHeadCell>Объем валюты</TableHeadCell>
          <TableHeadCell>Сумма валюты в RUB</TableHeadCell>
          {hasAnyWalletAddress && (
            <TableHeadCell>Адрес криптокошелька</TableHeadCell>
          )}
          {hasAnyActions && <TableHeadCell>Действия</TableHeadCell>}
        </TableRow>
      </TableHead>

      <TableBody>
        {providers.map((p) => (
          <TableRow key={p.id}>
            <TableCell>{p.epoch?.name}</TableCell>
            <TableCell>{p.organizationName}</TableCell>
            <TableCell>{formatNumber(p.price)}</TableCell>
            <TableCell>{formatNumber(p.amount)}</TableCell>
            <TableCell>{formatNumber(p.sum)}</TableCell>
            {hasAnyWalletAddress && (
              <TableCell>
                <CopyableText value={p.walletAddress} />
              </TableCell>
            )}
            {hasAnyActions && (
              <TableCell>
                {(p.actions?.length ?? 0) > 0 ? (
                  <ApplicationActions
                    actions={p.actions ?? []}
                    confirm={{
                      handler: openConfirmModalHandler,
                    }}
                    sign={signHandlers}
                    upload={getUploadHandlers(p.actions)}
                    form={getFormHandlers(p.actions)}
                    confirmDealUpload={getConfirmDealUpload(p.actions)}
                  />
                ) : null}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
